import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import ChessBoard from './ChessBoard';
import ChessSettings from './ChessSettings';
import {
  createInitialGameState,
  isValidMove,
  makeMove,
  getLegalMoves,
  toAlgebraic,
  getPieceColor,
  getPieceType,
} from './logic';
import {
  subscribeToGame,
  updateGameState,
  updateGameStatus,
  requestRematch,
  resetGameForRematch,
  saveGameToHistory,
  updateMatchStats,
} from '../../services/gameService';
import { addRecentOpponent } from '../../services/friendService';
import { getChessPreferences, getBoardColors, getPieceImagePath } from '../../services/chessPreferencesService';
import { database } from '../../services/firebase';
import { ref, update, serverTimestamp } from 'firebase/database';
import { safeString } from '../../utils/safeString';
import './Chess.css';

const Chess = ({ roomCode, roomData, user }) => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [promotionMove, setPromotionMove] = useState(null);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentRematchRequested, setOpponentRematchRequested] = useState(false);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [chessPreferences, setChessPreferences] = useState({
    boardStyle: 'green',
    pieceSet: 'neo',
    useImages: true,
  });
  const confettiShownRef = useRef(false);
  const gameRecordedRef = useRef(false);

  // Validate props
  if (!roomCode || !roomData || !user) {
    return (
      <div className="chess-container">
        <div className="error-message">Missing required data. Please try again.</div>
      </div>
    );
  }

  const isHost = roomData.host && roomData.host.userId === user.uid;
  const opponent = isHost ? roomData.guest : roomData.host;
  const opponentId = opponent?.userId || opponent?.uid || opponent?.id;

  // If opponent left, show waiting state
  if (!opponent) {
    const handleReturnToLobby = async () => {
      try {
        const roomRef = ref(database, `rooms/${roomCode}`);
        await update(roomRef, {
          status: 'waiting',
          gameSelected: null,
          lastActivity: serverTimestamp()
        });
        navigate(`/room/${roomCode}`);
      } catch (error) {
        console.error('Error returning to lobby:', error);
        navigate(`/room/${roomCode}`);
      }
    };

    return (
      <div className="chess-container">
        <div className="game-header">
          <h2>Chess</h2>
        </div>
        <div className="player-waiting-state">
          <div className="waiting-spinner"></div>
          <h3>Player left the room</h3>
          <p>Waiting for player to return...</p>
          <p className="waiting-hint">The game will resume when they come back</p>
          <button className="leave-button" onClick={handleReturnToLobby}>
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  const myColor = gameState ? (isHost ? gameState.hostColor : gameState.guestColor) : null;
  const opponentColor = myColor === 'white' ? 'black' : 'white';
  const isMyTurn = gameState && gameState.currentTurn === myColor;

  // Load chess preferences
  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user]);

  const loadPreferences = async () => {
    const prefs = await getChessPreferences(user.uid);
    setChessPreferences(prefs);
  };

  // Subscribe to game updates
  useEffect(() => {
    try {
      const unsubscribe = subscribeToGame(roomCode, async (game) => {
        if (game && game.gameState) {
          setGameState(game.gameState);

          // Check rematch requests
          if (game.rematchRequests) {
            const myRequest = !!game.rematchRequests[user.uid];
            const opponentRequest = opponentId ? !!game.rematchRequests[opponentId] : false;

            setRematchRequested(myRequest);
            setOpponentRematchRequested(opponentRequest);

            // If both players requested rematch, only the host resets the game
            if (myRequest && opponentRequest && isHost && game.gameState.isGameOver) {
              const newGameState = createInitialGameState(
                roomData.host.userId,
                roomData.guest.userId
              );

              await resetGameForRematch(
                roomCode,
                'chess',
                newGameState,
                {
                  host: roomData.host,
                  guest: roomData.guest,
                }
              );
            }
          } else {
            setRematchRequested(false);
            setOpponentRematchRequested(false);
          }
        } else if (game === null) {
          setError('Game not found. It may not have been created yet.');
        } else if (game && !game.gameState) {
          setError('Game data is missing gameState. Please try restarting the game.');
        }

        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up game subscription:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [roomCode, user.uid, opponentId, isHost, roomData]);

  // Trigger confetti on win
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameState.isDraw && !confettiShownRef.current) {
      const iWon = gameState.winner === myColor;

      if (iWon) {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
        });
        confettiShownRef.current = true;
      }
    }

    // Reset flags when game restarts
    if (gameState && !gameState.isGameOver) {
      confettiShownRef.current = false;
      gameRecordedRef.current = false;
    }
  }, [gameState, myColor]);

  // Save game to history when it ends
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameRecordedRef.current) {
      gameRecordedRef.current = true;

      const iWon = gameState.winner === myColor;
      const result = gameState.isDraw ? 'draw' : iWon ? 'won' : 'lost';

      // Validate opponent ID before proceeding
      if (!opponentId) {
        console.error('Could not determine opponent ID:', opponent);
        return;
      }

      // Convert winner color to user ID (if not a draw)
      const winnerUserId = gameState.isDraw
        ? 'draw'
        : (gameState.winner === myColor ? user.uid : opponentId);

      // Build game history object without undefined values
      const gameHistory = {
        gameType: 'chess',
        opponent: safeString(opponent?.displayName, 'Unknown'),
        opponentId: opponentId,
        result,
        winner: winnerUserId,
        isDraw: gameState.isDraw,
        roomCode,
      };

      // Only add drawReason if it exists and is not null/undefined
      if (gameState.drawReason != null) {
        gameHistory.drawReason = gameState.drawReason;
      }

      saveGameToHistory(user.uid, gameHistory);

      updateMatchStats(user.uid, 'chess', result);

      addRecentOpponent(
        user.uid,
        opponentId,
        safeString(opponent?.displayName, 'Unknown'),
        safeString(opponent?.photoURL, '/default-avatar.png'),
        'chess',
        result
      );

      updateGameStatus(roomCode, 'completed');
    }
  }, [gameState, myColor, user.uid, opponent, roomCode]);

  const handleSquareClick = async (row, col) => {
    if (!gameState || gameState.isGameOver || !isMyTurn) return;

    // Safety check for board position
    if (!gameState.board[row] || gameState.board[row][col] === undefined) return;

    const piece = gameState.board[row][col];
    const pieceColor = getPieceColor(piece);

    // If a piece is already selected
    if (selectedSquare) {
      const fromRow = selectedSquare.row;
      const fromCol = selectedSquare.col;

      // If clicking on the same square, deselect
      if (fromRow === row && fromCol === col) {
        setSelectedSquare(null);
        setLegalMoves([]);
        return;
      }

      // If clicking on another piece of the same color, select that piece instead
      if (pieceColor === myColor) {
        setSelectedSquare({ row, col });
        setLegalMoves(getLegalMoves(gameState, row, col));
        return;
      }

      // Try to make the move
      const isLegal = isValidMove(gameState, fromRow, fromCol, row, col);
      console.log('Move validation:', { fromRow, fromCol, toRow: row, toCol: col, isLegal });

      if (isLegal) {
        const movingPiece = gameState.board[fromRow] && gameState.board[fromRow][fromCol];
        if (!movingPiece) {
          console.error('No piece at selected position');
          setSelectedSquare(null);
          setLegalMoves([]);
          return;
        }

        const pieceType = getPieceType(movingPiece);

        // Check if it's a pawn promotion
        if (pieceType === 'P' && (row === 0 || row === 7)) {
          setPromotionMove({ fromRow, fromCol, toRow: row, toCol: col });
          setShowPromotionDialog(true);
          return;
        }

        // Make the move
        console.log('Making move...');
        const newGameState = makeMove(gameState, fromRow, fromCol, row, col);
        console.log('New game state:', newGameState);
        await updateGameState(roomCode, newGameState);

        setSelectedSquare(null);
        setLegalMoves([]);
      } else {
        console.log('Invalid move, deselecting');
        // Invalid move, deselect
        setSelectedSquare(null);
        setLegalMoves([]);
      }
    } else {
      // No piece selected, try to select one
      if (piece && pieceColor === myColor) {
        const moves = getLegalMoves(gameState, row, col);
        console.log('Selected piece at', { row, col }, 'Legal moves:', moves);
        setSelectedSquare({ row, col });
        setLegalMoves(moves);
      }
    }
  };

  const handlePromotion = async (promotionPiece) => {
    if (!promotionMove) return;

    const { fromRow, fromCol, toRow, toCol } = promotionMove;
    const newGameState = makeMove(gameState, fromRow, fromCol, toRow, toCol, promotionPiece);
    await updateGameState(roomCode, newGameState);

    setShowPromotionDialog(false);
    setPromotionMove(null);
    setSelectedSquare(null);
    setLegalMoves([]);
  };

  const handleRematch = async () => {
    await requestRematch(roomCode, user.uid);
  };

  const handleLeaveGame = async () => {
    try {
      const roomRef = ref(database, `rooms/${roomCode}`);
      await update(roomRef, {
        status: 'waiting',
        gameSelected: null,
        'host/gameSelection': null,
        'guest/gameSelection': null,
        lastActivity: serverTimestamp()
      });
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error leaving game:', error);
      navigate(`/room/${roomCode}`);
    }
  };

  const handleOfferDraw = async () => {
    if (!gameState || gameState.isGameOver) return;

    const newGameState = {
      ...gameState,
      drawOffer: { offeredBy: myColor }
    };
    await updateGameState(roomCode, newGameState);
  };

  const handleAcceptDraw = async () => {
    if (!gameState || gameState.isGameOver) return;

    const newGameState = {
      ...gameState,
      isGameOver: true,
      isDraw: true,
      drawReason: 'agreement',
      drawOffer: null
    };
    await updateGameState(roomCode, newGameState);
  };

  const handleDeclineDraw = async () => {
    if (!gameState) return;

    const newGameState = {
      ...gameState,
      drawOffer: null
    };
    await updateGameState(roomCode, newGameState);
  };

  const handleResign = async () => {
    if (!gameState || gameState.isGameOver) return;

    const newGameState = {
      ...gameState,
      isGameOver: true,
      winner: opponentColor,
      isDraw: false,
      drawReason: null
    };
    await updateGameState(roomCode, newGameState);
  };

  const getGameStatusMessage = () => {
    if (!gameState) return '';

    if (gameState.isGameOver) {
      if (gameState.isDraw) {
        const reasons = {
          'stalemate': 'Stalemate!',
          'insufficient-material': 'Draw by insufficient material',
          'fifty-move-rule': 'Draw by fifty-move rule',
          'agreement': 'Draw by agreement',
        };
        return reasons[gameState.drawReason] || 'Draw!';
      } else {
        const iWon = gameState.winner === myColor;
        if (gameState.winner === opponentColor && !gameState.inCheck) {
          return iWon ? 'Opponent resigned!' : 'You resigned';
        }
        return iWon ? 'You won! Checkmate!' : 'You lost. Checkmate.';
      }
    }

    if (gameState.inCheck) {
      return isMyTurn ? 'You are in check!' : 'Opponent is in check!';
    }

    return isMyTurn ? 'Your turn' : "Opponent's turn";
  };

  const PIECE_SYMBOLS = {
    wK: '♔', wQ: '♕', wR: '♖', wB: '♗', wN: '♘', wP: '♙',
    bK: '♚', bQ: '♛', bR: '♜', bB: '♝', bN: '♞', bP: '♟',
  };

  const PIECE_VALUES = {
    'K': 999, // King (shouldn't be captured, but highest value)
    'Q': 9,   // Queen
    'R': 5,   // Rook
    'B': 3,   // Bishop
    'N': 3,   // Knight
    'P': 1,   // Pawn
  };

  const getCapturedPiecesDisplay = (color) => {
    if (!gameState || !gameState.capturedPieces || !gameState.capturedPieces[color]) return null;
    const pieces = gameState.capturedPieces[color];

    // Sort pieces by value (most valuable first)
    const sortedPieces = [...pieces].sort((a, b) => {
      const valueA = PIECE_VALUES[a[1]] || 0;
      const valueB = PIECE_VALUES[b[1]] || 0;
      return valueB - valueA; // Descending order
    });

    const shouldUseImages = chessPreferences.useImages && chessPreferences.pieceSet !== 'unicode';

    return sortedPieces.map((piece, idx) => (
      <div
        key={idx}
        className={`captured-piece ${piece[0] === 'w' ? 'captured-white' : 'captured-black'}`}
        style={{ zIndex: sortedPieces.length - idx }}
      >
        {shouldUseImages ? (
          <img
            src={getPieceImagePath(chessPreferences.pieceSet, piece)}
            alt={PIECE_SYMBOLS[piece]}
            className="captured-piece-image"
          />
        ) : (
          PIECE_SYMBOLS[piece] || piece
        )}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="chess-container">
        <div className="loading-message">
          <div className="spinner-large"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chess-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="chess-container">
        <div className="loading-message">Waiting for game to start...</div>
      </div>
    );
  }

  const lastMove = gameState.moveHistory && gameState.moveHistory.length > 0
    ? gameState.moveHistory[gameState.moveHistory.length - 1]
    : null;

  const iWon = gameState.isGameOver && !gameState.isDraw && gameState.winner === myColor;
  const iLost = gameState.isGameOver && !gameState.isDraw && gameState.winner !== myColor;

  return (
    <div className="chess-container">
      {/* Header */}
      <div className="game-header">
        <h1 className="game-title">♟️ Chess 👑</h1>
      </div>

      {/* Game Info */}
      <div className="game-info">
        <div className="players-section">
          {/* Opponent */}
          <div className={`player-info opponent ${gameState.currentTurn === opponentColor ? 'active-player' : ''}`}>
            <img
              src={safeString(opponent?.photoURL, '')}
              alt={safeString(opponent?.displayName, 'Opponent')}
              className="player-avatar"
            />
            <div className="player-details">
              <span className="player-name">{safeString(opponent?.displayName, 'Opponent')}</span>
              <span className="player-color">{opponentColor === 'white' ? '⚪ White' : '⚫ Black'}</span>
            </div>
          </div>

          {/* Current Player */}
          <div className={`player-info current ${gameState.currentTurn === myColor ? 'active-player' : ''}`}>
            <img
              src={safeString(user?.photoURL, '')}
              alt={safeString(user?.displayName, 'You')}
              className="player-avatar"
            />
            <div className="player-details">
              <span className="player-name">You</span>
              <span className="player-color">{myColor === 'white' ? '⚪ White' : '⚫ Black'}</span>
            </div>
          </div>
        </div>

        {/* Move counter */}
        <div className="move-counter">
          Move {gameState.fullmoveNumber}
        </div>
      </div>

      {/* Turn Indicator */}
      {!gameState.isGameOver && (
        <div className="turn-indicator-section">
          <p className="turn-indicator">
            {isMyTurn ? "Your Turn!" : `${safeString(opponent?.displayName, 'Opponent')}'s Turn`}
          </p>
        </div>
      )}

      {/* Game Status */}
      {!gameState.isGameOver && (
        <div className="game-status">
          <p className="status-text">
            {isMyTurn ? "Make your move" : `Waiting for ${safeString(opponent?.displayName, 'Opponent')}...`}
          </p>
        </div>
      )}

      {/* Chess Settings Button - Moved below players, above board */}
      <div className="chess-settings-section">
        <button
          className="chess-settings-button"
          onClick={() => setShowSettings(true)}
          title="Customize Board & Pieces"
        >
          ⚙️ Board Settings
        </button>
      </div>

      {/* Draw Offer Dialog */}
      {gameState.drawOffer && gameState.drawOffer.offeredBy !== myColor && !gameState.isGameOver && (
        <div className="promotion-overlay">
          <div className="promotion-dialog draw-offer-dialog">
            <h3>Draw Offered</h3>
            <p>{safeString(opponent?.displayName, 'Opponent')} has offered a draw. Do you accept?</p>
            <div className="draw-offer-buttons">
              <button onClick={handleAcceptDraw} className="accept-draw-button">
                Accept Draw
              </button>
              <button onClick={handleDeclineDraw} className="decline-draw-button">
                Decline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chess Board with Captured Pieces */}
      <div className="chess-game-area">
        {/* Opponent's captured pieces (above board) */}
        <div className="captured-row opponent-captures">
          {getCapturedPiecesDisplay(myColor)}
        </div>

        <ChessBoard
          board={gameState.board}
          selectedSquare={selectedSquare}
          legalMoves={legalMoves}
          lastMove={lastMove}
          onSquareClick={handleSquareClick}
          disabled={!isMyTurn || gameState.isGameOver}
          myColor={myColor}
          inCheck={gameState.inCheck}
          currentTurn={gameState.currentTurn}
          boardColors={getBoardColors(chessPreferences.boardStyle)}
          pieceSet={chessPreferences.pieceSet}
          useImages={chessPreferences.useImages}
        />

        {/* My captured pieces (below board) */}
        <div className="captured-row my-captures">
          {getCapturedPiecesDisplay(opponentColor)}
        </div>
      </div>

      {/* Game Actions - Below the board */}
      {!gameState.isGameOver && (
        <div className="game-actions">
          <button
            className="offer-draw-button"
            onClick={handleOfferDraw}
            disabled={!isMyTurn || (gameState.drawOffer && gameState.drawOffer.offeredBy === myColor)}
          >
            {gameState.drawOffer && gameState.drawOffer.offeredBy === myColor
              ? 'Draw Offered'
              : 'Offer Draw'}
          </button>
          <button
            className="resign-button"
            onClick={handleResign}
            disabled={!isMyTurn}
          >
            Resign
          </button>
        </div>
      )}

      {/* Promotion Dialog */}
      {showPromotionDialog && (
        <div className="promotion-overlay">
          <div className="promotion-dialog">
            <h3>Promote Pawn</h3>
            <p>Choose a piece to promote to:</p>
            <div className="promotion-options">
              {['Q', 'R', 'B', 'N'].map((pieceType) => {
                const piece = (myColor === 'white' ? 'w' : 'b') + pieceType;
                const shouldUseImage = chessPreferences.useImages && chessPreferences.pieceSet !== 'unicode';
                const pieceNames = { Q: 'Queen', R: 'Rook', B: 'Bishop', N: 'Knight' };

                return (
                  <button
                    key={pieceType}
                    onClick={() => handlePromotion(pieceType)}
                    className="promotion-button"
                  >
                    <div className="promotion-piece">
                      {shouldUseImage ? (
                        <img
                          src={getPieceImagePath(chessPreferences.pieceSet, piece)}
                          alt={PIECE_SYMBOLS[piece]}
                          className="promotion-piece-image"
                        />
                      ) : (
                        PIECE_SYMBOLS[piece]
                      )}
                    </div>
                    <span>{pieceNames[pieceType]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameState.isGameOver && (
        <div className="game-over">
          <div className={`result-message ${iWon ? 'won' : iLost ? 'lost' : 'draw'}`}>
            {gameState.isDraw ? (
              <>
                <span className="result-icon">🤝</span>
                <h2>{getGameStatusMessage()}</h2>
                <p>Well played! Want another match?</p>
              </>
            ) : iWon ? (
              <>
                <span className="result-icon">🎉</span>
                <h2>You Won!</h2>
                <p>{getGameStatusMessage()}</p>
              </>
            ) : (
              <>
                <span className="result-icon">😞</span>
                <h2>You Lost</h2>
                <p>{getGameStatusMessage()}</p>
              </>
            )}
          </div>

          {/* Rematch Section */}
          <div className="rematch-section">
            {!rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button" onClick={handleRematch}>
                🔄 Request Rematch
              </button>
            )}

            {rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button waiting" disabled>
                ⏳ Waiting for opponent...
              </button>
            )}

            {opponentRematchRequested && !rematchRequested && (
              <button className="rematch-button accept" onClick={handleRematch}>
                ✓ Accept Rematch
              </button>
            )}

            {rematchRequested && opponentRematchRequested && (
              <button className="rematch-button starting" disabled>
                🎮 Starting rematch...
              </button>
            )}

            {opponentRematchRequested && !rematchRequested && (
              <p className="rematch-hint">Opponent wants a rematch!</p>
            )}
          </div>
        </div>
      )}

      {/* Leave Game Button - Always Visible */}
      <div className="leave-game-section">
        <button className="leave-button" onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>

      {/* Chess Settings Dialog */}
      {showSettings && (
        <ChessSettings
          user={user}
          onClose={() => setShowSettings(false)}
          onPreferencesChange={(prefs) => setChessPreferences(prefs)}
        />
      )}
    </div>
  );
};

export default Chess;
